import { useCallback } from 'react'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import { trackMapPinClicked } from 'tripPlanner/utils/chatAnalytics'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'

interface OfferActivation {
  offerId: string
  offerType?: string
  searchParams?: OfferClickSearchParams
}

/** One navigation path for pins and previews in both map presentations. */
export function useTripMapActivation() {
  const eventBus = useTripPlannerEventBus()
  const { chatId } = useChatStreamStatus()

  const activatePlace = useCallback((marker: MapMarkerComponent): boolean => {
    const placeName = marker.title || marker.label || ''
    if (marker.placeId) {
      eventBus.dispatch({ type: 'open_place_drawer', googlePlaceId: marker.placeId, placeName })
      return true
    }

    // Enlarged in-chat map pins already use their name as a place-search fallback.
    // Saved itinerary items must not resolve an unrelated place from a display label.
    const searchQuery = (marker.placeSearch ?? (marker.markerId?.startsWith('chat-map-pin-') ? placeName : '')).trim()
    if (!searchQuery) return false
    eventBus.dispatch({
      type: 'open_place_search_drawer',
      searchQuery,
      placeName: placeName || searchQuery,
    })
    return true
  }, [eventBus])

  const activateOffer = useCallback((data: OfferActivation) => {
    if (chatId) trackMapPinClicked(chatId, data.offerId)
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [chatId, eventBus])

  return { activatePlace, activateOffer }
}
